import React from 'react';
import {
  GripVertical,
  ChevronDown,
  ChevronRight,
  FileCode,
  Brackets,
  Plus,
  Trash2,
} from 'lucide-react';
import { TableColumn, SubfieldDefinition } from '../../../types/domain';
import { DATA_TYPES, getFieldIcon } from './types';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';

interface SchemaColumnRowProps {
  col: TableColumn;
  canEdit: boolean;
  draggedColId: string | null;
  isExpanded: boolean;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onToggleExpand: (id: string) => void;
  onUpdateField: (colId: string, field: keyof TableColumn, value: any) => void;
  onToggleRequired: (colId: string) => void;
  onUpdateValidationRules: (colId: string, patch: Record<string, any>) => void;
  onUpdateSampleValue: (colId: string, sampleValue: string) => void;
  onAddSubfield: (colId: string) => void;
  onDeleteColumn: (col: TableColumn) => void;
}

export const SchemaColumnRow: React.FC<SchemaColumnRowProps> = ({
  col,
  canEdit,
  draggedColId,
  isExpanded,
  onDragStart,
  onDragOver,
  onDrop,
  onToggleExpand,
  onUpdateField,
  onToggleRequired,
  onUpdateValidationRules,
  onUpdateSampleValue,
  onAddSubfield,
  onDeleteColumn,
}) => {
  const isComplex = col.data_type === 'object' || col.data_type === 'array';
  const subfields: SubfieldDefinition[] = col.validation_rules?.subfields || [];
  const hasSubfields = subfields.length > 0;
  const IconComponent = getFieldIcon(col.data_type, col.key, col.name);
  const sampleValue = col.validation_rules?.sample_value || '';
  const maxItems = col.validation_rules?.max_items;

  return (
    <tr
      draggable={canEdit}
      onDragStart={(e) => onDragStart(e, col.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, col.id)}
      className={`group hover:bg-cf-bg/50 transition-colors font-sans text-xs ${
        draggedColId === col.id ? 'opacity-40 bg-cf-orange/5' : ''
      }`}
    >
      {/* Dedicated Column 1: Navigation & Drag Handle */}
      <td className="py-2 px-2 align-middle text-center w-12 min-w-[48px] max-w-[48px]">
        <div className="flex items-center justify-center gap-0.5">
          <div
            className="text-cf-textMuted/40 group-hover:text-cf-textMuted cursor-grab active:cursor-grabbing p-0.5"
            title="Drag to rearrange"
          >
            <GripVertical size={13} />
          </div>
          {isComplex || hasSubfields ? (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => onToggleExpand(col.id)}
              title={isExpanded ? 'Collapse subfields' : 'Expand subfields'}
            >
              {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </Button>
          ) : (
            <span className="w-4" />
          )}
        </div>
      </td>

      {/* Dedicated Column 2: Field Label */}
      <td className="py-2 px-3 align-middle w-[240px] min-w-[220px]">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded bg-cf-bg border border-cf-border text-cf-textSecondary shrink-0">
            <IconComponent size={12} />
          </div>
          <div className="flex-1 min-w-[110px]">
            <Input
              size="sm"
              type="text"
              disabled={!canEdit}
              value={col.name}
              onChange={(e) => onUpdateField(col.id, 'name', e.target.value)}
              placeholder="Field Label"
            />
          </div>
          {isComplex && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-sans text-cf-textMuted bg-cf-surface border border-cf-border/80 shrink-0 whitespace-nowrap select-none"
              title={`${subfields.length} nested fields configured`}
            >
              {subfields.length} {subfields.length === 1 ? 'subfield' : 'subfields'}
            </span>
          )}
        </div>
      </td>

      {/* Dedicated Column 3: Field Key */}
      <td className="py-2 px-3 align-middle w-[180px] min-w-[160px]">
        <Input
          size="sm"
          type="text"
          disabled={!canEdit}
          value={col.key}
          onChange={(e) =>
            onUpdateField(
              col.id,
              'key',
              e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_')
            )
          }
          placeholder="field_key"
          className="text-cf-orange font-mono"
        />
      </td>

      {/* Column 4: Data Type */}
      <td className="py-2 px-3 align-middle w-[180px] min-w-[160px]">
        <Select
          size="sm"
          disabled={!canEdit}
          value={col.data_type}
          onChange={(e) => onUpdateField(col.id, 'data_type', e.target.value)}
          options={DATA_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          className={
            col.data_type === 'object'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-semibold'
              : col.data_type === 'array'
              ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400 font-semibold'
              : undefined
          }
        />
      </td>

      {/* Column 5: Requirement Toggle */}
      <td className="py-2 px-3 align-middle text-center w-[120px] min-w-[110px]">
        <Button
          variant={col.is_required ? 'primary' : 'secondary'}
          size="xs"
          disabled={!canEdit}
          onClick={() => onToggleRequired(col.id)}
          className={`min-w-[75px] shrink-0 whitespace-nowrap select-none ${
            col.is_required
              ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/25'
              : 'bg-cf-bg border-cf-border text-cf-textMuted hover:text-cf-textSecondary hover:bg-cf-surfaceHover'
          }`}
          title="Click to toggle requirement"
        >
          {col.is_required ? 'Required' : 'Optional'}
        </Button>
      </td>

      {/* Column 6: Sample / Constraints */}
      <td className="py-2 px-3 align-middle w-[270px] min-w-[260px]">
        {col.data_type === 'object' ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-cf-bg border border-cf-border text-cf-textSecondary font-sans text-xs select-none shrink-0 whitespace-nowrap">
            <FileCode size={12} className="text-emerald-400 shrink-0" />
            <span>{subfields.length} {subfields.length === 1 ? 'nested field' : 'nested fields'}</span>
          </span>
        ) : col.data_type === 'array' ? (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-cf-textMuted font-medium select-none shrink-0">
              Max items:
            </span>
            <div className="w-16 shrink-0">
              <Input
                size="sm"
                type="number"
                min={1}
                disabled={!canEdit}
                value={maxItems ?? ''}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  const parsed = raw === '' ? undefined : parseInt(raw, 10);
                  onUpdateValidationRules(col.id, {
                    max_items: parsed && parsed > 0 ? parsed : undefined,
                  });
                }}
                placeholder="None"
                className="text-center"
                title="Maximum allowed items in array (leave empty for unlimited)"
              />
            </div>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-textMuted font-sans text-xs select-none shrink-0 whitespace-nowrap">
              <Brackets size={11} className="text-blue-400 shrink-0" />
              <span>{subfields.length} {subfields.length === 1 ? 'field' : 'fields'}</span>
            </span>
          </div>
        ) : (
          <div className="w-full min-w-[180px] max-w-xs">
            <Input
              size="sm"
              type="text"
              disabled={!canEdit}
              value={sampleValue}
              onChange={(e) => onUpdateSampleValue(col.id, e.target.value)}
              placeholder={col.default_value || 'e.g. sample value'}
            />
          </div>
        )}
      </td>

      {/* Column 7: Actions */}
      <td className="py-2 px-3 align-middle text-right w-[110px] min-w-[100px]">
        <div className="flex items-center justify-end gap-1.5 shrink-0 whitespace-nowrap">
          {isComplex && (
            <Button
              variant="secondary"
              size="xs"
              disabled={!canEdit}
              onClick={() => onAddSubfield(col.id)}
              leftIcon={<Plus size={12} />}
              className="text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/30 whitespace-nowrap"
              title="Add Subfield to object schema"
            >
              Subfield
            </Button>
          )}
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => onDeleteColumn(col)}
              className="text-cf-textMuted hover:text-rose-400"
              title="Delete column"
            >
              <Trash2 size={13} />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};
